import React, { useState } from 'react';
import { useSiteConfig } from '@/lib/siteConfig';
import { gaEvent } from '@/lib/analytics';

export const SITE_URL = 'https://dotlinkapp.com';
// UTM-tagged so visits from shared posts show up as their own channel in Google Analytics
const shareUrl = (medium) => `${SITE_URL}/?utm_source=share&utm_medium=${medium}&utm_campaign=poster_caption`;
export const BUSINESS_EMAIL = 'business@dotlinkapp.com';
const POSTER = '/promo/dotlink-post.png';          // full-quality, for download
const POSTER_PREVIEW = '/promo/dotlink-post.jpg';  // light version shown on the page

const buildCaption = (pricing, email) => `Lost keys, a misplaced bag, a runaway pet? 📍
DotLink turns one QR sticker into an instant, private way for anyone to reach you — no app to install, no sharing your phone number.

✅ Vehicles · Assets · Pets
✅ Call & chat right in the browser
✅ Free for your first vehicle, PRO just ₹${pricing.pro_price_inr}/${pricing.period_label}

🔗 ${shareUrl('caption')}
✉️ ${email}

#DotLink #QRCode #SmartTag #LostAndFound #PetSafety #VehicleSafety`;

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// "Spread the word" block on the landing page: the promo poster plus
// one-tap download / copy caption / copy link / share / email actions.
const ShareSection = () => {
  const { config } = useSiteConfig();
  const CAPTION = buildCaption(config.pricing, config.contact.email || BUSINESS_EMAIL);
  const [copied, setCopied] = useState('');

  const doCopy = async (key, text) => {
    gaEvent('share', { method: key, content_type: 'site', item_id: 'landing' });
    if (await copyText(text)) {
      setCopied(key);
      setTimeout(() => setCopied(''), 1800);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'DotLink', text: CAPTION, url: shareUrl('native') });
        return;
      } catch {
        // dismissed — fall through to copy
      }
    }
    doCopy('caption', CAPTION);
  };

  const btn = 'btn-press rounded-full px-5 py-2.5 text-sm font-semibold transition-all';

  return (
    <section id="share" className="mb-16">
      <div className="bg-white rounded-[2rem] shadow-xl overflow-hidden grid md:grid-cols-2">
        <div className="bg-gradient-to-br from-indigo-900 to-purple-900 flex items-center justify-center p-6 md:p-10">
          <img
            src={POSTER_PREVIEW}
            alt="DotLink promo post — One scan. Instant connection."
            loading="lazy"
            decoding="async"
            width="540"
            height="675"
            className="w-full max-w-sm rounded-3xl shadow-2xl"
          />
        </div>

        <div className="p-8 md:p-10 flex flex-col justify-center">
          <p className="text-xs font-bold tracking-widest text-indigo-600 mb-2">SPREAD THE WORD</p>
          <h2 className="text-3xl font-bold text-gray-800 mb-3">Share DotLink with someone who needs it</h2>
          <p className="text-gray-600 mb-5">
            Download the poster, copy the ready-made caption and post it on WhatsApp, Instagram or anywhere your people are.
          </p>

          <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5 font-sans max-h-52 overflow-auto">
            {CAPTION}
          </pre>

          <div className="flex flex-wrap gap-3 mb-6">
            <a
              href={POSTER}
              onClick={() => gaEvent('share', { method: 'poster_download', content_type: 'site', item_id: 'landing' })}
              download="dotlink-post.png"
              className={`${btn} bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg`}
            >
              ⬇ Download poster
            </a>
            <button onClick={() => doCopy('caption', CAPTION)} className={`${btn} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
              {copied === 'caption' ? '✓ Caption copied' : 'Copy caption'}
            </button>
            <button onClick={() => doCopy('link', shareUrl('link'))} className={`${btn} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
              {copied === 'link' ? '✓ Link copied' : 'Copy link'}
            </button>
            <button onClick={share} className={`${btn} bg-indigo-50 text-indigo-700 hover:bg-indigo-100`}>
              Share
            </button>
          </div>

          <div className="text-sm text-gray-600 space-y-1">
            <p>
              🌐 <a href={SITE_URL} className="font-semibold text-indigo-600 hover:underline">dotlinkapp.com</a>
            </p>
            <p>
              ✉️ Business &amp; partnerships:{' '}
              <a href={`mailto:${BUSINESS_EMAIL}`} className="font-semibold text-indigo-600 hover:underline">{BUSINESS_EMAIL}</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShareSection;
